import { useEffect, useState } from "react";
import type { Alert, ActivityEvent, DealSummary, TaskItem } from "@edge/shared";
import { CockpitHeader } from "../cockpit/CockpitHeader";
import { TasksRail } from "../cockpit/TasksRail";
import { AlertsRail } from "../cockpit/AlertsRail";
import { ChatBar } from "../cockpit/ChatBar";
import { ChatExchange, type ChatTurn } from "../chat/ChatExchange";
import { BreadcrumbBar } from "./BreadcrumbBar";
import { TabStrip, type FocusTab } from "./TabStrip";
import { renderFocusTab } from "./tabs";
import { NotesPanel } from "./NotesPanel";
import { useFocusData } from "../../hooks/useFocusData";
import type { PersonalTasksState } from "../../hooks/usePersonalTasks";
import type { CompletedTasksState } from "../../hooks/useCompletedTasks";
import { postChat, ApiError, createDealNote, logFocusOpened } from "../../api";

interface FocusViewProps {
  dealId: string;
  /** Active tab — controlled by App so it can be mirrored into the URL (QW-1). */
  activeTab: FocusTab;
  /** Change the active tab (also updates the URL via App's writer). */
  onSelectTab: (tab: FocusTab) => void;
  /** All open CRM tasks (filtered here to the focused deal). */
  crmTasks: TaskItem[];
  /** Shared personal-tasks state from App. Same list shown in cockpit MY LIST. */
  personal: PersonalTasksState;
  /** Shared completed history. */
  completedPersonal: CompletedTasksState;
  /** CRM task write actions — shared with App so writes refresh both views. */
  crmActions: {
    complete: (task: TaskItem) => Promise<void>;
    reschedule: (task: TaskItem, newDate: string) => Promise<void>;
    create: (input: import("../../hooks/useCrmTaskActions").CrmTaskCreateInput) => Promise<void>;
    edit: import("../shared/CrmTaskEditDialog").CrmTaskEditDialogProps["onSave"];
  };
  /** All deals — used by the "+ CRM TASK" dialog's linked-deal picker. */
  deals: DealSummary[];
  /** Global watchtower alerts (filtered here to the focused deal name). */
  alerts: Alert[];
  watchtowerLoading: boolean;
  /** Whole-cockpit activity feed — currently unused in focus mode (Phase 1b). */
  activity: ActivityEvent[];
  /** Shared per-tab chat session id from App (conversation memory). */
  sessionId: string;
  onBack: () => void;
  /**
   * Hand chat directives (navigate, propose_crm_task_create, propose_deal_update)
   * back to App so write-dialogs render in one place. App's handler decides what
   * each directive does.
   */
  onChatDirective: (directive: import("../../api").ChatDirective | null | undefined) => void;
  /** Open the Pipeline kanban modal — shown via the header icon. */
  onOpenPipeline?: () => void;
  /** Open the Tasks macro modal — shown via the header icon. */
  onOpenTasks?: () => void;
  /** Open the Calendar modal — shown via the header icon. */
  onOpenCalendar?: () => void;
  /** Open the standalone Email workspace — shown via the header icon. */
  onOpenEmail?: () => void;
  /** Open the New Intake modal — kept visible in focus too (capture any time). */
  onNewIntake?: () => void;
  /** Dismiss a Watchtower alert from the focus-mode rail. Routes to the same
   *  worker endpoint as the cockpit-centerpiece dismissal. */
  onDismissAlert: (
    alertId: string,
    kind: "solved" | "removed",
    note?: string,
  ) => void;
}

export function FocusView({
  dealId,
  activeTab,
  onSelectTab,
  crmTasks,
  personal,
  completedPersonal,
  crmActions,
  deals,
  alerts,
  watchtowerLoading,
  sessionId,
  onBack,
  onChatDirective,
  onOpenPipeline,
  onOpenTasks,
  onOpenCalendar,
  onOpenEmail,
  onNewIntake,
  onDismissAlert,
}: FocusViewProps) {
  const { detail, notes, loading, reload: reloadFocus } = useFocusData(dealId);
  const [chatTurn, setChatTurn] = useState<ChatTurn | null>(null);

  // Log focus-mode open whenever the focused deal changes. Drives the
  // Watchtower `deal_neglected` rule. Fire-and-forget; errors swallowed
  // in the api client.
  useEffect(() => {
    if (!dealId) return;
    void logFocusOpened(dealId, detail?.name ?? undefined);
    // detail?.name intentionally not in deps: we want one log per deal
    // entry, not a fresh log when detail finishes loading on the same deal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Filter tasks + alerts to this deal.
  const dealTasks = crmTasks.filter((t) => t.dealId === dealId);
  const dealAlerts = detail
    ? alerts.filter((a) => {
        if (!a.dealName) return false;
        const dealKey = detail.name.toUpperCase();
        const alertKey = a.dealName.toUpperCase();
        // Names in alerts are typically shorter ("MASCOLA" vs "Louis Mascola - San Pedro Con Refi")
        return dealKey.includes(alertKey) || alertKey.includes(dealKey.split(" ")[0]);
      })
    : [];

  const submitChat = async (message: string) => {
    const id = crypto.randomUUID();
    setChatTurn({ id, question: message, response: undefined });
    try {
      const response = await postChat({
        message,
        session_id: sessionId,
        context: {
          view: "focus",
          deal_id: dealId,
          deal_name: detail?.name,
        },
      });
      setChatTurn({ id, question: message, response });
      onChatDirective(response.directive);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "unknown error";
      setChatTurn({ id, question: message, response: null, error: msg });
    }
  };

  return (
    <>
      <div className="scan-beam" />
      <div className="corner-readout tl">SYS.NET // SECURE TUNNEL // ENCRYPTED</div>
      <div className="corner-readout br">
        EDGE.OS // © WESTEDGE FUNDING INC // CA DRE 02382109
      </div>

      <div className="cockpit" data-mode="focus">
        <CockpitHeader
          onNewIntake={onNewIntake}
          onOpenPipeline={onOpenPipeline}
          onOpenTasks={onOpenTasks}
          onOpenCalendar={onOpenCalendar}
          onOpenEmail={onOpenEmail}
        />
        <BreadcrumbBar detail={detail} loading={loading} onBack={onBack} />

        <div className="leftcol">
          <TasksRail
            crmOnly
            crmTasks={dealTasks}
            personalTasks={personal.tasks}
            onCompletePersonal={personal.complete}
            onSnoozePersonal={personal.snooze}
            onCreatePersonal={personal.create}
            onUpdatePersonal={personal.update}
            onReschedulePersonal={personal.reschedule}
            completedPersonal={completedPersonal}
            personalVersion={personal.version}
            onCompleteCrm={crmActions.complete}
            onRescheduleCrm={crmActions.reschedule}
            onEditCrm={crmActions.edit}
          />
          {/* QW-7: Watchtower now lives in the bottom of the left column as a
              compact, internally-scrolling box (swapped with Notes). */}
          <AlertsRail
            alerts={dealAlerts}
            loading={watchtowerLoading}
            deals={deals}
            onCreateCrmTask={crmActions.create}
            onAddToMyList={personal.create}
            onSaveNote={async ({ deal_id, content }) => {
              await createDealNote(deal_id, { content });
              // If the note was saved to the currently-focused deal, refresh
              // the Notes panel so the new note shows up without a manual reload.
              if (deal_id === dealId) reloadFocus();
            }}
            onFeedback={(id, kind, note) => onDismissAlert(id, kind, note)}
          />
        </div>

        <div className="center-stage">
          <div className="deal-workspace">
            <TabStrip active={activeTab} onSelect={onSelectTab} />
            {renderFocusTab(activeTab, { dealId, detail, loading })}
          </div>
          {chatTurn && (
            <ChatExchange turn={chatTurn} onDismiss={() => setChatTurn(null)} />
          )}
          <ChatBar
            onSubmit={submitChat}
            placeholder={
              detail
                ? `Ask about ${detail.name.split(" - ")[0]}…`
                : "Ask about this deal…"
            }
          />
        </div>

        {/* QW-7: Notes takes the full-height right rail (grid-area: alerts),
            swapped with Watchtower above. */}
        <NotesPanel notes={notes} loading={loading} />
      </div>
    </>
  );
}

